import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft, BarChart3, TrendingUp, TrendingDown, Minus,
  Target, Flame, AlertTriangle, CheckCircle, XCircle,
  Clock, Zap, Crown, ChevronRight
} from "lucide-react";

import { getApiBaseUrl } from "@/services/api";
const API_URL = getApiBaseUrl();

/**
 * Banking Analytics Dashboard
 * Implements Sections 5, 6, 7, 17, 18 from CLARIAS Banking AI prompt
 */
const BankingAnalyticsPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const isPro = user?.role === "PRO" || user?.banking_role === "PRO";

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await fetch(`${API_URL}/api/banking/analytics/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      }
    } catch (err) {
      console.error("Failed to fetch analytics:", err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "improving": return <TrendingUp className="w-5 h-5 text-emerald-500" />;
      case "declining": return <TrendingDown className="w-5 h-5 text-red-500" />;
      default: return <Minus className="w-5 h-5 text-amber-500" />;
    }
  };

  const getSectionStatusBadge = (status) => {
    switch (status) {
      case "safe": return <Badge className="bg-emerald-100 text-emerald-700">Safe</Badge>;
      case "borderline": return <Badge className="bg-amber-100 text-amber-700">Borderline</Badge>;
      case "critical": return <Badge className="bg-red-100 text-red-700">Critical</Badge>;
      default: return <Badge>Unknown</Badge>;
    }
  };

  const getProgressBarColor = (accuracy, cutoff) => {
    if (accuracy >= cutoff + 20) return "bg-emerald-500";
    if (accuracy >= cutoff) return "bg-amber-500";
    return "bg-red-500";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen bg-slate-50 p-4">
        <Card className="max-w-md mx-auto mt-20">
          <CardContent className="p-6 text-center">
            <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-slate-900 mb-2">No Data Yet</h2>
            <p className="text-slate-600 mb-4">Complete some practice sessions to see your analytics.</p>
            <Button onClick={() => navigate('/banking')}>Start Practicing</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 text-white">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/banking')}
              className="bg-white/10 hover:bg-white/20 text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Performance Analytics</h1>
                <p className="text-emerald-200 text-sm">Your Banking Exam Preparation Insights</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">

        {/* Overall Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-0">
            <CardContent className="p-4">
              <p className="text-emerald-100 text-xs mb-1">Total MCQs</p>
              <p className="text-3xl font-bold">{analytics.overall?.total_questions || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0">
            <CardContent className="p-4">
              <p className="text-blue-100 text-xs mb-1">Overall Accuracy</p>
              <p className="text-3xl font-bold">{analytics.overall?.overall_accuracy || 0}%</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Flame className="w-5 h-5" />
                <p className="text-amber-100 text-xs">Streak</p>
              </div>
              <p className="text-3xl font-bold">{analytics.streak?.current || 0} days</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-violet-600 text-white border-0">
            <CardContent className="p-4">
              <p className="text-purple-100 text-xs mb-1">Practice Time</p>
              <p className="text-3xl font-bold">{analytics.overall?.total_practice_time_mins || 0}m</p>
            </CardContent>
          </Card>
        </div>

        {/* Beat Yesterday - Section 5 */}
        <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white border-0">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <Target className="w-5 h-5 text-amber-400" />
                  Beat Yesterday
                </h3>
                <p className="text-slate-400 text-sm">Daily performance comparison</p>
              </div>
              {getStatusIcon(analytics.beat_yesterday?.status)}
            </div>
            <div className="grid grid-cols-3 gap-6">
              <div className="text-center">
                <p className="text-slate-400 text-xs mb-1">Yesterday</p>
                <p className="text-3xl font-bold">{analytics.beat_yesterday?.yesterday_score || 0}%</p>
              </div>
              <div className="text-center">
                <p className="text-slate-400 text-xs mb-1">Today</p>
                <p className="text-3xl font-bold text-emerald-400">{analytics.beat_yesterday?.today_score || 0}%</p>
              </div>
              <div className="text-center">
                <p className="text-slate-400 text-xs mb-1">Change</p>
                <p className={`text-3xl font-bold ${analytics.beat_yesterday?.difference >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {analytics.beat_yesterday?.difference >= 0 ? '+' : ''}{analytics.beat_yesterday?.difference || 0}%
                </p>
              </div>
            </div>
            <div className="mt-4 text-center">
              <Badge className={`${
                analytics.beat_yesterday?.status === 'improving' ? 'bg-emerald-500/20 text-emerald-400' :
                analytics.beat_yesterday?.status === 'declining' ? 'bg-red-500/20 text-red-400' :
                'bg-amber-500/20 text-amber-400'
              } border-0`}>
                {analytics.beat_yesterday?.status?.toUpperCase() || 'STABLE'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Section Balance Radar - Section 7 */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-lg text-slate-900">Section Balance Radar</h3>
                <p className="text-sm text-slate-500">Performance by section vs cutoffs</p>
              </div>
              <Badge variant="outline">Section 7</Badge>
            </div>
            
            <div className="space-y-4">
              {Object.entries(analytics.section_balance || {}).map(([section, data]) => (
                <div key={section} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900 capitalize">{section}</span>
                      {getSectionStatusBadge(data.status)}
                    </div>
                    <span className="text-sm text-slate-600">
                      {data.accuracy}% (Cutoff: {data.cutoff}%)
                    </span>
                  </div>
                  <div className="relative">
                    <Progress 
                      value={data.accuracy} 
                      className="h-3"
                    />
                    {/* Cutoff marker */}
                    <div 
                      className="absolute top-0 h-3 w-0.5 bg-red-500"
                      style={{ left: `${data.cutoff}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-slate-400">
                    <span>{data.attempted} attempted</span>
                    <span>{data.correct} correct, {data.wrong} wrong</span>
                  </div>
                </div>
              ))}
            </div>
            
            {Object.keys(analytics.section_balance || {}).length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <p>No section data yet. Start practicing to see your balance.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Negative Marking Intelligence - Section 18 */}
        <Card className={`${analytics.negative_analysis?.should_reduce_guessing ? 'border-red-300 bg-red-50/50' : ''}`}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
                  {analytics.negative_analysis?.should_reduce_guessing && (
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                  )}
                  Negative Marking Analysis
                </h3>
                <p className="text-sm text-slate-500">Track marks lost to wrong attempts</p>
              </div>
              <Badge variant="outline">Section 18</Badge>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-slate-50 rounded-xl p-3 text-center">
                <p className="text-xs text-slate-500 mb-1">Attempted</p>
                <p className="text-xl font-bold text-slate-900">{analytics.negative_analysis?.total_attempted || 0}</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-3 text-center">
                <p className="text-xs text-slate-500 mb-1">Correct</p>
                <p className="text-xl font-bold text-emerald-600">{analytics.negative_analysis?.correct || 0}</p>
              </div>
              <div className="bg-red-50 rounded-xl p-3 text-center">
                <p className="text-xs text-slate-500 mb-1">Wrong</p>
                <p className="text-xl font-bold text-red-600">{analytics.negative_analysis?.wrong || 0}</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-3 text-center">
                <p className="text-xs text-slate-500 mb-1">Marks Lost</p>
                <p className="text-xl font-bold text-amber-600">-{analytics.negative_analysis?.marks_lost || 0}</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-3 text-center">
                <p className="text-xs text-slate-500 mb-1">Efficiency</p>
                <p className="text-xl font-bold text-blue-600">{analytics.negative_analysis?.attempt_efficiency || 0}%</p>
              </div>
            </div>
            
            {analytics.negative_analysis?.should_reduce_guessing && (
              <div className="mt-4 bg-red-100 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">High Wrong Attempt Rate Detected!</p>
                  <p className="text-sm text-red-700">Reduce guessing. Skip uncertain questions. Focus on: "Attempt fewer, attempt better."</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* PRO Features */}
        {isPro ? (
          <>
            {/* Exam Readiness - Section 9 */}
            {analytics.exam_readiness && (
              <Card className="bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-emerald-500/10 border-emerald-200">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-bold text-lg text-slate-900">Exam Readiness Timeline</h3>
                      <p className="text-sm text-slate-500">Your preparation phase status</p>
                    </div>
                    <Badge className="bg-emerald-500 text-white">PRO</Badge>
                  </div>
                  
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-slate-600">Overall Readiness</span>
                        <span className="font-bold text-emerald-600">{analytics.exam_readiness.overall_readiness}%</span>
                      </div>
                      <Progress value={analytics.exam_readiness.overall_readiness} className="h-3" />
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                      <span className="text-sm">Concept Base</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {analytics.exam_readiness.phase === 'speed_building' ? (
                        <div className="w-5 h-5 border-2 border-amber-500 rounded-full flex items-center justify-center">
                          <div className="w-2 h-2 bg-amber-500 rounded-full" />
                        </div>
                      ) : (
                        <div className="w-5 h-5 border-2 border-slate-300 rounded-full" />
                      )}
                      <span className="text-sm">Speed Building</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-slate-300 rounded-full" />
                      <span className="text-sm">Mock Dominance</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Daily Trend Chart */}
            {analytics.daily_trend && analytics.daily_trend.length > 0 && (
              <Card>
                <CardContent className="p-5">
                  <h3 className="font-bold text-lg text-slate-900 mb-4">30-Day Performance Trend</h3>
                  <div className="h-40 flex items-end gap-1">
                    {analytics.daily_trend.slice(-30).map((day, idx) => (
                      <div
                        key={idx}
                        className="flex-1 bg-emerald-500 rounded-t"
                        style={{ height: `${day.accuracy}%` }}
                        title={`${day.date}: ${day.accuracy}%`}
                      />
                    ))}
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-slate-400">
                    <span>30 days ago</span>
                    <span>Today</span>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          /* Upgrade Prompt for Free Users */
          <Card className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10 border-amber-200">
            <CardContent className="p-6 text-center">
              <Crown className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h3 className="font-bold text-lg text-slate-900 mb-2">Unlock Full Analytics</h3>
              <p className="text-slate-600 mb-4">
                Upgrade to PRO to access Exam Readiness Timeline, Daily Trends, 
                Attempt Personality Analysis, and AI-powered strategy recommendations.
              </p>
              <Button
                onClick={() => navigate('/account-settings')}
                className="bg-gradient-to-r from-amber-500 to-orange-500 text-white"
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade to PRO
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4">
          <Button
            variant="outline"
            className="h-auto py-4"
            onClick={() => navigate('/banking/ai-coach')}
          >
            <div className="text-left">
              <p className="font-medium">AI Coach Analysis</p>
              <p className="text-xs text-slate-500">Get personalized insights</p>
            </div>
            <ChevronRight className="w-5 h-5 ml-auto" />
          </Button>
          <Button
            variant="outline"
            className="h-auto py-4"
            onClick={() => navigate('/banking')}
          >
            <div className="text-left">
              <p className="font-medium">Continue Practice</p>
              <p className="text-xs text-slate-500">Improve your scores</p>
            </div>
            <ChevronRight className="w-5 h-5 ml-auto" />
          </Button>
        </div>

      </div>
    </div>
  );
};

export default BankingAnalyticsPage;
